import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssetManager from '../services/AssetManager';
import logger from '../utils/logger';
import ScheduleEntry from '../models/ScheduleEntry';
import { useChangeTracking } from './useChangeTracking';
import { useSnackbar } from '../contexts/SnackbarContext';
import { v4 as uuidv4 } from 'uuid';

type BaselineContext = {
    isLiveMode: boolean;
    draftName: string | null | undefined;
    selectedYear: number | null | undefined;
    selectedPeriodId?: string | number | null;
    selectedMonth?: any | null;
    selectedWeek?: any | null;
    onIdSwap?: (tempId: string, savedEntry: any) => void;
};

const isTemporaryScheduleEntryId = (id: string) => (
    id.startsWith('temp-') || id.startsWith('item-') || id.startsWith('drag-created-')
);

/**
 * useScheduleSaveManager centralizes:
 * - Baseline fetching from the API (via AssetManager)
 * - Baseline initialization in useChangeTracking (even when empty)
 * - Saving a single entry (create/update) with baseline merge
 * - Saving a set of entries using changeLog (create/update/delete) with baseline sync
 */
export function useScheduleSaveManager(user: any, ctx: BaselineContext) {
    const assetManager = useMemo(() => AssetManager.getInstance(), []);
    const { showSuccess, showError } = useSnackbar();

    // Build a stable baseline version key from context
    const baselineVersion = useMemo(() => {
        const draftSegment = ctx.isLiveMode ? 'live' : `draft:${ctx.draftName || 'unnamed'}`;
        const yearSegment = `year:${ctx.selectedYear ?? 'none'}`;
        const periodSegment = `period:${ctx.selectedPeriodId ?? 'none'}`;
        const monthSegment = `month:${(ctx.selectedMonth && ctx.selectedMonth.month) || 'none'}`;
        const weekSegment = `week:${(ctx.selectedWeek && ctx.selectedWeek.week) || 'none'}`;
        return `${draftSegment}-${yearSegment}-${periodSegment}-${monthSegment}-${weekSegment}`;
    }, [ctx.isLiveMode, ctx.draftName, ctx.selectedYear, ctx.selectedPeriodId, ctx.selectedMonth, ctx.selectedWeek]);

    // Local baseline state fetched from the server for the current context
    const [baselineItems, setBaselineItems] = useState<any[]>([]);
    const [isFetchingBaseline, setIsFetchingBaseline] = useState<boolean>(false);
    const [baselineError, setBaselineError] = useState<string | null>(null);
    const lastLoadedVersionRef = useRef<string | null>(null);
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const createClientIdsRef = useRef<Map<string, string>>(new Map());

    // useChangeTracking without auto-baseline; we'll initialize manually so we can allow empty baselines
    const {
        changeLog,
        addLogEntry,
        calculateNetChanges,
        getNetChanges,
        serverBaseline,
        initializeChangeTracking,
        mergeIntoBaseline,
        removeFromBaseline,
        getChangeIdSets,
        acknowledgeChangeIds,
        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    } = useChangeTracking(user);

    // Expose lightweight counters for pending changes via ID sets
    const getChangeIdCounts = useCallback(() => {
        try {
            const { createdIds, modifiedIds, deletedIds } = getChangeIdSets();
            return {
                created: createdIds.size,
                modified: modifiedIds.size,
                deleted: deletedIds.size
            };
        } catch (e) {
            return { created: 0, modified: 0, deleted: 0 };
        }
    }, [getChangeIdSets]);

    const hasPendingChangesByIdSets = useCallback(() => {
        const counts = getChangeIdCounts();
        return (counts.created + counts.modified + counts.deleted) > 0;
    }, [getChangeIdCounts]);

    const getCreateClientId = useCallback((localId: string) => {
        const existing = createClientIdsRef.current.get(localId);
        if (existing) return existing;
        const clientId = `scheduler-create:${uuidv4()}`;
        createClientIdsRef.current.set(localId, clientId);
        return clientId;
    }, []);

    const fetchBaseline = useCallback(async () => {
        setIsFetchingBaseline(true);
        setBaselineError(null);
        try {
            const filters: any = {};
            if (!ctx.isLiveMode && ctx.draftName) {
                filters.draft_name = ctx.draftName;
            }
            if (ctx.selectedPeriodId) {
                filters.timePeriodId = ctx.selectedPeriodId;
            }
            if (ctx.selectedYear != null) {
                filters.year = ctx.selectedYear;
            }

            logger.debug('[useScheduleSaveManager] Fetching baseline with filters:', filters);
            const entries = await assetManager.fetchScheduleEntries(filters);
            setBaselineItems(entries);

            // Initialize baseline inside useChangeTracking even if empty
            initializeChangeTracking(entries);
            lastLoadedVersionRef.current = baselineVersion;
            logger.debug('[useScheduleSaveManager] Baseline initialized:', {
                count: entries.length,
                version: baselineVersion
            });
        } catch (err: any) {
            logger.error('[useScheduleSaveManager] Failed to fetch baseline:', err);
            setBaselineError(err?.message || 'Failed to fetch baseline');
        } finally {
            setIsFetchingBaseline(false);
        }
    }, [assetManager, ctx.isLiveMode, ctx.draftName, ctx.selectedPeriodId, ctx.selectedYear, initializeChangeTracking, baselineVersion]);

    // Refresh baseline when the context key changes
    useEffect(() => {
        if (lastLoadedVersionRef.current !== baselineVersion) {
            // fetchBaseline();
        }
    }, [baselineVersion, fetchBaseline]);


    // Save a single entry (create or update). Returns the saved ScheduleEntry.
    const saveSingleEntry = useCallback(async (editedDataObj: any, originalItem?: any) => {
        // Determine if create vs update
        const isAddOperation = originalItem && originalItem.id && String(originalItem.id).startsWith('temp-add-');

        // Normalize to ScheduleEntry shape, attach draft name when in draft mode
        const entryData = { ...(editedDataObj || {}) } as any;
        if (!ctx.isLiveMode && ctx.draftName) {
            (entryData as any).draft_name = ctx.draftName;
        }

        const entryObj = new ScheduleEntry(entryData);

        let savedEntry: ScheduleEntry;
        if (isAddOperation) {
            // For creation, ensure no temp id is sent
            const createData = entryObj.toObject();
            delete (createData as any).id;
            if (!(createData as any).client_id) {
                (createData as any).client_id = getCreateClientId(String(originalItem.id));
            }
            savedEntry = await assetManager.createScheduleEntry(createData);
            createClientIdsRef.current.delete(String(originalItem.id));
        } else {
            savedEntry = await assetManager.updateScheduleEntry(entryObj.id, entryObj);
        }

        // Merge the saved entry into baseline for immediate consistency
        try {
            mergeIntoBaseline([savedEntry]);
        } catch (e) {
            logger.warn('[useScheduleSaveManager] mergeIntoBaseline failed after single save:', e);
        }

        return savedEntry;
    }, [assetManager, ctx.isLiveMode, ctx.draftName, mergeIntoBaseline, getCreateClientId]);

    // Persist one snapshot. Successful operations are acknowledged individually so
    // a later retry cannot replay writes that have already reached the server.
    const persistEntireSet = useCallback(async (currentScheduleItems: any[], _unusedChangeLogEntries?: any[]) => {
        logger.debug('[useScheduleSaveManager] saveEntireSet (ID-set mode) called', {
            currentLen: currentScheduleItems?.length || 0,
            baselineVersion
        });

        // Read tracked IDs from change tracking
        const { createdIds, modifiedIds, deletedIds } = getChangeIdSets();

        const createdIdsArr = Array.from(createdIds);
        const modifiedIdsArr = Array.from(modifiedIds);
        const deletedIdsArr = Array.from(deletedIds);

        // Resolve items by ID from the provided current list
        const byId = new Map<string, any>((Array.isArray(currentScheduleItems) ? currentScheduleItems : []).map((it: any) => [String(it?.id), it]));
        const createdItems = createdIdsArr.map(id => byId.get(String(id))).filter(Boolean);
        const modifiedItems = modifiedIdsArr.map(id => byId.get(String(id))).filter(Boolean);

        logger.debug('[useScheduleSaveManager] Persisting by change sets', {
            createCount: createdItems.length,
            modifyCount: modifiedItems.length,
            deleteCount: deletedIdsArr.length
        });

        const createCount = createdItems.length;
        const modifyCount = modifiedItems.length;
        const deleteCount = deletedIdsArr.length;
        const totalCount = createCount + modifyCount + deleteCount;

        type SaveResult = {
            kind: 'create' | 'recover-create' | 'modify' | 'delete';
            localId: string;
            saved?: any;
        };
        const operations: Array<Promise<SaveResult>> = [];

        deletedIdsArr.forEach((id) => {
            const idStr = String(id);
            if (isTemporaryScheduleEntryId(idStr)) {
                operations.push(Promise.resolve<SaveResult>({ kind: 'delete', localId: idStr }));
                return;
            }
            operations.push(
                assetManager.deleteScheduleEntry(idStr)
                    .then(() => ({ kind: 'delete', localId: idStr }))
            );
        });

        createdItems.forEach((item: any) => {
            const data = item instanceof ScheduleEntry ? item.toObject() : { ...item };
            const tempId = String((item as any)?.id);
            if (!ctx.isLiveMode && ctx.draftName) {
                (data as any).draft_name = ctx.draftName;
            }
            delete (data as any).id;
            // Reuse one opaque idempotency key for this temporary entry.
            if (!(data as any).client_id) {
                (data as any).client_id = getCreateClientId(tempId);
            }
            operations.push(
                assetManager.createScheduleEntry(data)
                    .then(saved => ({ kind: 'create', localId: tempId, saved }))
            );
        });

        modifiedItems.forEach((item: any) => {
            const data = item instanceof ScheduleEntry ? item : new ScheduleEntry(item);
            if (!ctx.isLiveMode && ctx.draftName) {
                (data as any).draft_name = ctx.draftName;
            }
            const id = String((data as any).id);
            const isTemporaryId = isTemporaryScheduleEntryId(id);
            if (isTemporaryId) {
                const createData = data.toObject();
                if (!ctx.isLiveMode && ctx.draftName) {
                    (createData as any).draft_name = ctx.draftName;
                }
                delete (createData as any).id;
                if (!(createData as any).client_id) {
                    (createData as any).client_id = getCreateClientId(id);
                }
                operations.push(
                    assetManager.createScheduleEntry(createData)
                        .then(saved => ({ kind: 'recover-create', localId: id, saved }))
                );
                return;
            }
            operations.push(
                assetManager.updateScheduleEntry(id, data)
                    .then(saved => ({ kind: 'modify', localId: id, saved }))
            );
        });

        const settledResults = await Promise.all(operations.map(async operation => {
            try {
                return { status: 'fulfilled' as const, value: await operation };
            } catch (reason) {
                return { status: 'rejected' as const, reason };
            }
        }));
        const successful = settledResults
            .filter((result): result is { status: 'fulfilled'; value: SaveResult } => result.status === 'fulfilled')
            .map(result => result.value);
        const failed = settledResults
            .filter((result): result is { status: 'rejected'; reason: unknown } => result.status === 'rejected');

        const successfulCreates = successful.filter(result => (
            result.kind === 'create' || result.kind === 'recover-create'
        ));
        const successfulModifies = successful.filter(result => result.kind === 'modify');
        const successfulDeletes = successful.filter(result => result.kind === 'delete');
        const savedEntries = [...successfulCreates, ...successfulModifies]
            .map(result => result.saved)
            .filter(Boolean);

        successfulCreates.forEach(({ localId, saved }) => {
            createClientIdsRef.current.delete(localId);
            if (saved?.id && typeof ctx.onIdSwap === 'function') {
                try {
                    ctx.onIdSwap(localId, saved);
                } catch (error) {
                    logger.warn('[useScheduleSaveManager] Failed to swap a saved temporary ID:', error);
                }
            }
        });

        acknowledgeChangeIds({
            createdIds: successfulCreates
                .filter(result => result.kind === 'create')
                .map(result => result.localId),
            modifiedIds: [
                ...successfulModifies,
                ...successfulCreates.filter(result => result.kind === 'recover-create')
            ].map(result => result.localId),
            deletedIds: successfulDeletes.map(result => result.localId)
        });
        if (savedEntries.length > 0) {
            mergeIntoBaseline(savedEntries);
        }
        if (successfulDeletes.length > 0) {
            const deletedIds = successfulDeletes.map(result => result.localId);
            deletedIds.forEach(id => createClientIdsRef.current.delete(id));
            removeFromBaseline(deletedIds);
            setBaselineItems(prev => prev.filter(entry => deletedIds.indexOf(String(entry?.id)) === -1));
        }

        if (failed.length > 0) {
            const error: any = failed[0]?.reason;
            const backendMessage = (error && (error.response?.data?.error || error.message)) || 'Save failed';
            const savedCount = successful.length;
            try {
                showError(savedCount > 0
                    ? `${backendMessage} ${savedCount} other change${savedCount === 1 ? ' was' : 's were'} saved.`
                    : backendMessage);
            } catch {}
            throw error;
        }

        if (totalCount > 0) {
            try {
                showSuccess(`Saved ${createCount + modifyCount + deleteCount} changes`);
            } catch {}
        }

        return successful.map(result => result.saved).filter(Boolean);
    }, [assetManager, ctx, getChangeIdSets, getCreateClientId, acknowledgeChangeIds, mergeIntoBaseline, removeFromBaseline, baselineVersion, showError, showSuccess]);

    // Serialize saves. A queued invocation reads the pending ID sets only when it
    // starts, after the previous invocation has acknowledged its successful writes.
    const saveEntireSet = useCallback((currentScheduleItems: any[], changeLogEntries?: any[]) => {
        const run = saveQueueRef.current.then(
            () => persistEntireSet(currentScheduleItems, changeLogEntries),
            () => persistEntireSet(currentScheduleItems, changeLogEntries)
        );
        saveQueueRef.current = run.then(() => undefined, () => undefined);
        return run;
    }, [persistEntireSet]);

    // Manager-level handleSaveChanges: compute changes and persist; then update baseline/reset
    const handleSaveChanges = useCallback(async (currentScheduleItems: any[], customSaveHandler?: (items: any[], changes: any[]) => Promise<any>, onResetComplete?: () => void) => {
        logger.debug('[useScheduleSaveManager] handleSaveChanges start', {
            currentLen: currentScheduleItems?.length || 0,
            baselineVersion
        });

        // Persist using ID sets tracking; ignore net-changes
        const persist = async () => {
            if (customSaveHandler && typeof customSaveHandler === 'function') {
                // Maintain signature but pass empty changes (we rely on ID sets)
                return customSaveHandler(currentScheduleItems, []);
            }
            return saveEntireSet(currentScheduleItems, []);
        };

        await persist();

        try {
            // After successful save, reset local baseline state
            setBaselineItems(prev => prev);
        } catch (e) {
            logger.warn('[useScheduleSaveManager] Baseline post-save sync warning:', e);
        }

        if (onResetComplete && typeof onResetComplete === 'function') {
            onResetComplete();
        }
    }, [saveEntireSet, baselineVersion]);

    // Backward-compatible alias for components expecting this name
    const saveChangesToAPI = saveEntireSet;

    return {
        // Change tracking passthrough
        changeLog,
        addLogEntry,
        calculateNetChanges,
        handleSaveChanges,
        serverBaseline,
        getChangeIdCounts,
        hasPendingChangesByIdSets,

        // Manager extras
        saveSingleEntry,
        handleSaveSchedule: saveSingleEntry,
        saveEntireSet,
        saveChangesToAPI,
        isFetchingBaseline,
        baselineError,
        baselineVersion,

        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    };
}

export default useScheduleSaveManager;


